import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useLayoutEffect, useRef, useState, } from "react";
import hash from "object-hash";
import { Icon } from "../../icon";
import { useTheme } from "styled-components";
import { KeyBoardKeys } from "../../types";
import { IconWrapper } from "./IconWrapper";
import { TabContainer } from "./Tab.styles";
import { TabSubTitle } from "./TabSubTitle.styles";
import { TitleContainer } from "./TitleContainer.styles";
import { StyledTabTitle } from "./TabTitle.styles";
// utils
import { isWithBackground } from "./utils";
import { makeDataTestIdGetter } from "../../utils";
const useIsomorphicLayoutEffect = typeof document !== "undefined" ? useLayoutEffect : useEffect;
export function Tab({ index, title, subTitle, alignment, selected, disabled, stylingType, symbol, onTabChange, changeOnMouseOver, isActive, onClick, isLastHovered, statusLeft, titleTag, statusRight, withoutIcon, dataTestIdPrefix, }) {
    const [focused, setFocused] = useState(false);
    const [isMultiLine, setIsLabelMultiLine] = useState(false);
    const theme = useTheme();
    const titleRef = useRef(null);
    function setLabelDimensions() {
        if (titleRef.current) {
            const { offsetHeight } = titleRef.current;
            setIsLabelMultiLine(offsetHeight > 20);
        }
    }
    useEffect(() => {
        window.addEventListener("resize", setLabelDimensions);
        return () => window.removeEventListener("resize", setLabelDimensions);
    });
    useIsomorphicLayoutEffect(() => {
        setLabelDimensions();
    });
    function getSymbol() {
        if (alignment === "vertical" && !stylingType) {
            return "chevron_right";
        }
        return "";
    }
    function renderIcon() {
        const iconColor = disabled
            ? `rgba(${theme.colors.transparentBlack},0.26)`
            : selected && alignment === "vertical" && stylingType !== "withIcons"
                ? theme.colors.primaryLight
                : "inherit";
        let icon = symbol ? (_jsx(Icon, { width: "1.25rem", withHitSpace: alignment === "vertical", symbol: symbol || (withoutIcon ? "" : getSymbol()), color: iconColor }, `icon-${symbol}`)) : null;
        if ((!symbol && !getSymbol()) ||
            (!selected && alignment === "vertical" && stylingType === "withIcon") ||
            isWithBackground(stylingType)) {
            icon = null;
        }
        return ((icon || stylingType === "withIcon") && (_jsx(IconWrapper, Object.assign({ withIcon: !!icon, stylingType: stylingType, alignment: alignment }, { children: icon }), hash({ index, symbol }))));
    }
    function renderContent() {
        return (_jsxs(TitleContainer, Object.assign({ stylingType: stylingType }, { children: [_jsx(StyledTabTitle, Object.assign({ ref: titleRef, multiLine: isMultiLine, alignment: alignment, as: titleTag, selected: selected, stylingType: stylingType }, { children: title })), _jsx(TabSubTitle, Object.assign({ disabled: disabled }, { children: subTitle }))] }), hash({ index, title })));
    }
    function handleOnChange() {
        if (disabled)
            return;
        onTabChange({ index, title, subTitle });
    }
    function handleOnClick(e) {
        if (disabled)
            return;
        if (typeof handleOnChange === "function")
            handleOnChange();
        if (typeof onClick === "function")
            onClick(e);
    }
    function handleOnMouseDown() {
        setFocused(false);
    }
    function handleOnBlur() {
        setFocused(false);
    }
    function handleOnKeyUp(event) {
        if (focused) {
            if (event.key === KeyBoardKeys.Enter ||
                event.key === KeyBoardKeys.Space ||
                event.key === " ") {
                onTabChange({ index, title, subTitle });
            }
        }
        if (!disabled && event.key === KeyBoardKeys.Tab) {
            setFocused(true);
        }
    }
    const left = statusLeft ? (_jsx(IconWrapper, Object.assign({ withIcon: true, stylingType: stylingType, alignment: alignment }, { children: _jsx(_Fragment, { children: statusLeft }) }), hash({ index, symbol }))) : (renderIcon());
    const right = statusRight && (_jsx(IconWrapper, Object.assign({ withIcon: true, stylingType: stylingType, alignment: alignment }, { children: _jsx(_Fragment, { children: statusRight }) }), hash({ index, symbol })));
    const elements = [left, renderContent(), right];
    const tabIndex = disabled ? -1 : 0;
    const getDataTestId = makeDataTestIdGetter(dataTestIdPrefix);
    if (stylingType !== "withIcon" &&
        stylingType !== "withIcons" &&
        stylingType !== "withAdditionalElement") {
        elements.reverse();
    }
    return (_jsx(TabContainer, Object.assign({ isMultiLine: isMultiLine, alignment: alignment, disabled: disabled, focused: focused, selected: selected, stylingType: stylingType, tabIndex: tabIndex, onMouseDown: handleOnMouseDown, onKeyUp: handleOnKeyUp, onBlur: handleOnBlur, onClick: handleOnClick, isActive: isActive, tabTitle: title, isLastHovered: isLastHovered, changeOnMouseOver: changeOnMouseOver }, (changeOnMouseOver ? { onMouseEnter: handleOnChange } : {}), { "data-testid": getDataTestId("tab-input") }, { children: elements })));
}
